import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { buildDashboardColumnDescriptions, resolveDashboardColumnDescription } from "../services/dashboardColumnDescriptions";

export default function DashboardColumnTooltip({ shellRef, metadata }: {
  shellRef: React.RefObject<HTMLDivElement>;
  metadata: unknown;
}) {
  const id = useId();
  const dismiss = useRef<() => void>(() => {});
  const index = useMemo(() => buildDashboardColumnDescriptions(metadata), [metadata]);
  const [tooltip, setTooltip] = useState<{ text: string; left: number; top: number } | null>(null);

  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let active: Element | null = null;
    let activeDescription: string | null = null;
    let previousDescription: string | null = null;
    const hide = () => {
      clearTimeout(timer);
      if (active) {
        if (previousDescription === null) active.removeAttribute("aria-describedby");
        else active.setAttribute("aria-describedby", previousDescription);
      }
      active = null;
      activeDescription = null;
      setTooltip(null);
    };
    const show = (event: Event) => {
      const match = resolveDashboardColumnDescription(event.target, shell, index);
      if (match?.header === active) return;
      hide();
      if (!match) return;
      active = match.header;
      activeDescription = match.description;
      previousDescription = active.getAttribute("aria-describedby");
      timer = setTimeout(() => {
        if (!active?.isConnected) return hide();
        // Re-resolve after the delay in case the SDK recycled a virtual header.
        const current = resolveDashboardColumnDescription(active, shell, index);
        if (!current || current.description !== match.description) return hide();
        const rect = active.getBoundingClientRect();
        active.setAttribute("aria-describedby", [previousDescription, id].filter(Boolean).join(" "));
        setTooltip({ text: match.description, left: Math.max(8, Math.min(rect.left, window.innerWidth - 336)), top: Math.max(8, Math.min(rect.bottom + 8, window.innerHeight - 160)) });
      }, 450);
    };
    const leave = (event: MouseEvent | FocusEvent) => {
      if (active && event.relatedTarget instanceof Node && active.contains(event.relatedTarget)) return;
      if (event.relatedTarget instanceof Node && document.getElementById(id)?.contains(event.relatedTarget)) return;
      hide();
    };
    dismiss.current = hide;
    const scroll = (event: Event) => {
      if (event.target instanceof Node && document.getElementById(id)?.contains(event.target)) return;
      hide();
    };
    const keydown = (event: KeyboardEvent) => { if (event.key === "Escape") hide(); };
    const observer = new MutationObserver(() => {
      if (active && (!active.isConnected || resolveDashboardColumnDescription(active, shell, index)?.description !== activeDescription)) hide();
    });
    observer.observe(shell, { childList: true, subtree: true, characterData: true });
    shell.addEventListener("mouseover", show);
    shell.addEventListener("focusin", show);
    shell.addEventListener("mouseout", leave);
    shell.addEventListener("focusout", leave);
    shell.addEventListener("pointerdown", hide);
    window.addEventListener("scroll", scroll, true);
    window.addEventListener("resize", hide);
    window.addEventListener("keydown", keydown);
    return () => {
      hide();
      dismiss.current = () => {};
      observer.disconnect();
      shell.removeEventListener("mouseover", show);
      shell.removeEventListener("focusin", show);
      shell.removeEventListener("mouseout", leave);
      shell.removeEventListener("focusout", leave);
      shell.removeEventListener("pointerdown", hide);
      window.removeEventListener("scroll", scroll, true);
      window.removeEventListener("resize", hide);
      window.removeEventListener("keydown", keydown);
    };
  }, [index, shellRef, id]);

  return tooltip ? createPortal(
    <div id={id} role="tooltip" onMouseLeave={() => dismiss.current()} style={{ position: "fixed", left: tooltip.left, top: tooltip.top, zIndex: 10000, maxWidth: "min(320px, calc(100vw - 16px))", maxHeight: 144, overflow: "auto", padding: "12px 16px", borderRadius: 6, background: "#071722", color: "#e6edf3", border: "1px solid #46535e", boxShadow: "0 4px 16px #0004", fontSize: 14, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
      {tooltip.text}
    </div>, document.body,
  ) : null;
}
